// Steps: a task whose "references" edge points at another task is a step of
// it (the task page's "Make sub-tasks", Prime's subtasks). Nothing is stored
// beyond the edge, so any list can set steps in under their parent when both
// are on it, and a step whose parent is elsewhere stands on its own.
import type { Edge } from '@/db/types'
import type { TaskResource } from '@/kinds/task/apply'

export interface Stepped {
  task: TaskResource
  child: boolean
}

/** Parents first, each followed by its children that are also in the list; the rest in place. */
export const nest = <T extends { id: string }>(
  list: T[],
  edges: Edge[],
  relation: 'references' | 'belongs_to',
): { item: T; child: boolean }[] => {
  const ids = new Set(list.map((t) => t.id))
  const byId = new Map(list.map((t) => [t.id, t]))
  const children = new Map<string, T[]>()
  const isChild = new Set<string>()
  for (const e of edges) {
    if (e.relation !== relation || e.from_id === e.to_id || !ids.has(e.from_id) || !ids.has(e.to_id)) continue
    isChild.add(e.from_id)
    children.set(e.to_id, [...(children.get(e.to_id) ?? []), byId.get(e.from_id)!])
  }
  const order = new Map(list.map((t, i) => [t.id, i]))
  const out: { item: T; child: boolean }[] = []
  for (const t of list) {
    if (isChild.has(t.id)) continue
    out.push({ item: t, child: false })
    const kids = [...(children.get(t.id) ?? [])].sort((a, b) => order.get(a.id)! - order.get(b.id)!)
    for (const c of kids) out.push({ item: c, child: true })
  }
  return out
}

/** Parents first, each followed by its steps that are also in the list; the rest in place. */
export const withSteps = (list: TaskResource[], edges: Edge[]): Stepped[] =>
  nest(list, edges, 'references').map(({ item, child }) => ({ task: item, child }))

/** The steps of one task, from every task and edge known. */
export const stepsOf = (id: string, tasks: TaskResource[], edges: Edge[]): TaskResource[] => {
  const ids = new Set(
    edges.filter((e) => e.relation === 'references' && e.to_id === id).map((e) => e.from_id),
  )
  return tasks.filter((t) => ids.has(t.id) && !t.deleted_at)
}
